import React, { useEffect, useState } from 'react';

import { DropdownButton } from '../DropdownButton';
import {
  Container,
  PaginationInfo,
  PaginationItem,
  Pagination as Pag,
} from './PaginationStyles';
import {
  DEFAULT_PAGINATION_MOREPAGES_SEPARATOR,
  DEFAULT_PAGINATION_OPTIONS,
  DEFAULT_PAGINATION_PAGE_DELTA,
} from './constants';
import { PaginationProperties, dropDirection } from './types';
import { ArrowDown } from 'components/Icons/ArrowDown';
import { ArrowUp } from 'components/Icons/ArrowUp';

const getItems = (currentPage: number, size: number) => {
  const delta = DEFAULT_PAGINATION_PAGE_DELTA;
  const rangeWithDots: (number | string)[] = [];
  const current = currentPage;
  const left = current - delta;
  const right = current + delta + 1;
  let prevItems = 0;

  const range = Array.from(Array(size + 1).keys()).filter(
    (page: number) =>
      page && (page === 1 || page === size || (page >= left && page < right))
  );

  for (let i of range) {
    if (prevItems) {
      if (i - prevItems === 2) {
        rangeWithDots.push(prevItems + 1);
      } else if (i - prevItems !== 1) {
        rangeWithDots.push(DEFAULT_PAGINATION_MOREPAGES_SEPARATOR);
      }
    }
    rangeWithDots.push(i);
    prevItems = i;
  }

  return rangeWithDots;
};

export const Pagination: React.FC<PaginationProperties> = ({
  pageSize,
  pageOptions,
  pageIndex,
  size,
  setPageSize,
  gotoPage,
  selector,
  controlledPagination,
  pageCount,
}) => {
  const [active, setActive] = useState<boolean>(false);
  const [drop, setDrop] = useState<dropDirection>('down');
  const data = getItems(pageIndex, pageOptions.length);

  useEffect(() => {
    const elem = document.querySelector('.modal.show');
    if (elem) {
      setDrop('up');
    }
  }, []);

  const startRow = pageSize * pageIndex + 1;
  let endRow: number = startRow + pageSize - 1;

  if (size < endRow) {
    endRow = size;
  }

  useEffect(() => {
    const pageCount = Number(pageIndex ?? 0) * pageSize;
    const maxNumberOfPages = Math.ceil(size / pageSize);

    if (controlledPagination) {
      if (pageIndex > maxNumberOfPages - 1) {
        gotoPage(maxNumberOfPages - 1);
      }
    } else {
      if (size < pageCount) {
        gotoPage(0);
      }
    }
  }, [size, pageIndex, pageSize]);

  let pageButtons: boolean = true;

  if (controlledPagination) {
    if (size < pageSize && pageOptions.length > 0) {
      pageButtons = false;
    }
  }

  const DropdownIcon = () => {
    const size = '12px';
    const fill = '#a8a8a8';
    const sharedStyle = { marginLeft: '10px' };

    return !active ? (
      <ArrowDown fill={fill} size={size} style={sharedStyle} />
    ) : (
      <ArrowUp fill={fill} size={size} style={sharedStyle} />
    );
  };

  return (
    <>
      {size > pageSize || controlledPagination ? (
        <Container>
          <PaginationInfo>
            Rows per page:
            <DropdownButton
              drop={drop}
              variant={'tertiary'}
              customIcon={true}
              onClick={() => {
                setActive(!active);
              }}
              onSelect={(e: any) => {
                setPageSize(e.value);
                if (selector) {
                  setTimeout(() => {
                    const elem = document.querySelector(selector);
                    elem?.scrollTo(0, 0);
                  }, 0);
                }
              }}
              list={DEFAULT_PAGINATION_OPTIONS}
            >
              {pageSize}
              <DropdownIcon />
            </DropdownButton>
          </PaginationInfo>
          {pageButtons ? (
            <Pag>
              {data.map((page: number | string, i: number) => {
                return (
                  <PaginationItem
                    data-testid={`pagination-page-${i + 1}`}
                    key={i}
                    onClick={() => {
                      if (page !== DEFAULT_PAGINATION_MOREPAGES_SEPARATOR) {
                        gotoPage(+page - 1);
                      }
                    }}
                    active={+page === +pageIndex + 1}
                  >
                    {page}
                  </PaginationItem>
                );
              })}
            </Pag>
          ) : null}
          <PaginationInfo>
            {`Showing: ${startRow} - ${endRow} of ${size}`}
          </PaginationInfo>
        </Container>
      ) : null}
    </>
  );
};
